import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router';

export function useSearchFilterDropdown(facetType: string) {
  const [searchParams] = useSearchParams();

  const selectedFilterValues = searchParams.getAll(facetType);
  const hasSelectedValues = selectedFilterValues.length > 0;

  const holderRef = useRef<HTMLDivElement>(null);

  const dropdownIsOpenRef = useRef(false);
  const hasOtherOpenDropdownsRef = useRef(false);

  const filterStateRef = useRef(
    hasSelectedValues &&
      !dropdownIsOpenRef.current &&
      !hasOtherOpenDropdownsRef.current
      ? 'PINNED'
      : 'UNPINNED',
  );

  const toggleDropdown = useCallback(
    (open: boolean) => {
      dropdownIsOpenRef.current = open;

      if (!holderRef.current) {
        throw new Error(
          `Search filter dropdown for ${facetType} does not exist.`,
        );
      }

      const { dataset } = holderRef.current;

      const filterIsPinned = dataset.facetState === 'PINNED';
      const filterIsUnpinned = dataset.facetState === 'UNPINNED';
      const otherOpenDropdowns = Array.from(
        document.querySelectorAll(
          `[data-facet-state] details[open]:not(:has([data-facet-type=${facetType}])`,
        ),
      );

      hasOtherOpenDropdownsRef.current = otherOpenDropdowns.length > 0;

      if (!open) {
        if (hasSelectedValues && filterIsUnpinned) {
          dataset.facetState = 'QUEUED';

          if (!hasOtherOpenDropdownsRef.current) {
            const filtersToPin = [
              ...document.querySelectorAll(
                '[data-facet-state="QUEUED"]:has(input:checked:not([value=""]))',
              ),
            ] as HTMLDivElement[];

            filtersToPin.forEach((node) => {
              // eslint-disable-next-line no-param-reassign
              node.dataset.facetState = 'PINNED';
            });
          }
        } else if (!hasSelectedValues && filterIsPinned) {
          dataset.facetState = 'QUEUED';

          if (!hasOtherOpenDropdownsRef.current) {
            const filtersToUnpin = [
              ...document.querySelectorAll(
                '[data-facet-state="QUEUED"]:not(:has(input:checked:not([value=""])))',
              ),
            ] as HTMLDivElement[];

            filtersToUnpin.forEach((node) => {
              // eslint-disable-next-line no-param-reassign
              node.dataset.facetState = 'UNPINNED';
            });
          }
        }
      }
    },
    [facetType, hasSelectedValues],
  );

  return { holderRef, filterState: filterStateRef.current, toggleDropdown };
}
